import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	QueryList,
	ViewChildren,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { SavedPin } from '@models/teranet';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SalePinActions } from '@state/sale_pins';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	CondoUnit,
	MatterClientType,
	MatterFileType,
	MatterPropertyDetails,
	MatterTitle,
	PropertyType,
	UnitType,
} from '@models/matter';
import {
	SyncDataRequest,
	TeranetRepoService,
} from '@repos/teranet/teranet-repo.service';
import LogRocket from 'logrocket';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { BaseClient, ClientCreationData, ClientType } from '@models/clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	CreateClientMiniModalComponent,
	CreateClientMiniModalData,
} from '../../../fields/client/create/modal/modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ClientGuarantorType } from '../../../../../shared/components/matter-user-select-or-search/matter-user-select-or-search.component';
import { ICON } from '@core/util/icon';
import { TitleSyncFormComponent } from './title-sync-form/title-sync-form.component';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { titleCase } from '@core/util/name-formatting.utils';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import { TestHooks } from './sync-inner-form.hooks';
import { BaseComponent } from '@core/base.component';
import {
	SyncInnerFormState,
	SyncInnerFormStateSelector,
} from './sync-inner-form.selector';

export interface SelectedSeller {
	type: ClientType;
	id: string;
}

export interface SelectedSellerOption {
	sync: boolean;
	add: boolean;
	selected: string;
	newValue: string;
}

interface selectedTitle {
	key?: string;
	primary: boolean;
	pin: selectedPinOption;
	description: selectedPinOption;
	instrumentNumber: selectedPinOption;
}

interface selectedPinOption {
	override: boolean;
	newValue: string;
	oldValue: string;
}

export enum SyncStatus {
	SYNCING = 'syncing',
	SYNCED = 'synced',
	NOT_SYNCING = 'not-syncing',
}

interface VM {
	clientTypeText: string;
	capitalizedClientTypeText: string;
	parentPins: SavedPin[];
	sellers: BaseClient[];
	sellerActions: SelectedSellerOption[];
	submitted: boolean;
	isCondo: boolean;
	syncedTitles: { [pinId: string]: MatterTitle };
	showComparisonMap: { [pinId: string]: boolean };
}

const defaultVM = {
	clientTypeText: '',
	capitalizedClientTypeText: '',
	parentPins: null,
	sellers: null,
	sellerActions: [],
	submitted: false,
	isCondo: false,
	syncedTitles: {},
	showComparisonMap: {},
};

@UntilDestroy()
@Component({
	selector: 'app-teranet-sync-inner-form',
	templateUrl: './sync-inner-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SyncInnerFormComponent
	extends BaseComponent<VM>
	implements OnInit
{
	testHooks = TestHooks;
	@Output() onError: EventEmitter<string> = new EventEmitter();
	@Output() onSyncStatusUpdate: EventEmitter<SyncStatus> = new EventEmitter();
	@Output() onSyncSuccess: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() onHasUpdates: EventEmitter<boolean> = new EventEmitter<boolean>();

	@Input() form: NgForm;
	@Input() submit$: Observable<void>;

	ICON = ICON;

	public showMatterClientTypes: [
		ClientGuarantorType.SELLER,
		ClientGuarantorType.BORROWER,
	];

	public set error(_error) {
		this.onError.emit(_error);
	}

	public set syncStatus(_syncStatus: SyncStatus) {
		this.onSyncStatusUpdate.emit(_syncStatus);
	}

	@Select(SyncInnerFormStateSelector.getSyncInnerFormState)
	syncInnerFormState$: Observable<SyncInnerFormState>;

	@SelectSnapshot(SelectedMatter.id) matterId;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@ViewSelectSnapshot(SelectedMatter.propertyDetails)
	propertyDetails: MatterPropertyDetails;
	@SelectSnapshot(MatterTitlesState.getPrimaryTitle) primaryTitle: MatterTitle;

	@ViewChildren('titleSyncForm')
	titleSyncForms: QueryList<TitleSyncFormComponent>;

	constructor(
		private teranetRepo: TeranetRepoService,
		private matDialog: MatDialog,
		private matterClientDispatch: MatterClientDispatch,
	) {
		super(defaultVM);

		this.VM = {
			isCondo: this.propertyDetails?.propertyType?.type === PropertyType.CONDO,
		};

		this.syncInnerFormState$
			.pipe(
				untilDestroyed(this),
				map((state) => {
					const { parentPins, sellers } = state;
					if (!(this.VM.parentPins || this.VM.sellers)) {
						this.VM = {
							parentPins,
							sellers,
						};
						const primaryPin = parentPins.find((x) => x.primary);

						// if there are sellers add ability to sync
						if (primaryPin?.parcels?.length > 0) {
							this.VM = {
								sellerActions: primaryPin?.parcels[0].owners.map((owner) => ({
									add: !(this.VM.sellers?.length > 0),
									newValue: titleCase(owner.name),
									selected: null,
									sync: false,
								})),
							};
						}

						const updatedSyncedTitles = { ...this.VM.syncedTitles };
						const updatedShowComparisonMap = { ...this.VM.showComparisonMap };

						parentPins.forEach((pin) => {
							updatedSyncedTitles[pin.pin] = null;
							updatedShowComparisonMap[pin.pin] = !this.VM.isCondo;
						});

						this.VM = {
							syncedTitles: updatedSyncedTitles,
							showComparisonMap: updatedShowComparisonMap,
						};
					}
				}),
			)
			.subscribe();
	}

	ngOnInit(): void {
		this.submit$.pipe(untilDestroyed(this)).subscribe(() => this.onSubmit());
		this.syncStatus = SyncStatus.NOT_SYNCING;

		this.VM = {
			clientTypeText:
				this.fileType === MatterFileType.REFINANCE ? 'owner' : 'vendor',
			capitalizedClientTypeText:
				this.fileType === MatterFileType.REFINANCE ? 'Owner' : 'Vendor',
		};
	}

	onTitleSync(pinId: string, title: MatterTitle) {
		const updatedSyncedTitles = { ...this.VM.syncedTitles };

		updatedSyncedTitles[pinId] = title;

		this.VM = {
			syncedTitles: updatedSyncedTitles,
		};
	}

	onClientSelectUpdate(index, $event) {
		const updatedSellerActions = [...this.VM.sellerActions];

		updatedSellerActions[index].add = $event.create;
		updatedSellerActions[index].selected = $event.selected;

		this.VM = {
			sellerActions: updatedSellerActions,
		};
	}

	togglePinAction(action: selectedTitle) {
		if (action.pin.override) {
			action.pin.override = false;
			action.description.override = false;
			action.instrumentNumber.override = false;
		} else {
			action.pin.override = true;
			action.description.override = true;
			action.instrumentNumber.override = true;
		}
	}

	toggleDescriptionAction(action: selectedTitle) {
		action.description.override = !action.description.override;
		action.pin.override =
			action.description.override || action.instrumentNumber.override;
	}

	toggleInstrumentAction(action: selectedTitle) {
		action.instrumentNumber.override = !action.instrumentNumber.override;
		action.pin.override =
			action.description.override || action.instrumentNumber.override;
	}

	@Dispatch()
	setPinToSynced(pin: string) {
		return new SalePinActions.SetPinSynced(pin);
	}

	async onSubmit() {
		this.error = null;
		this.VM = {
			submitted: true,
		};

		if (this.form.invalid) {
			this.error = 'Please fill in all required fields.';

			return;
		}

		this.syncStatus = SyncStatus.SYNCING;

		const syncRequest: SyncDataRequest = {
			updateClients: [],
			titlesToSync: [],
		};

		this.VM.sellerActions
			?.filter((x) => x.sync)
			?.forEach((action) => {
				syncRequest.updateClients.push({
					name: action.newValue,
					id: action.selected,
					add: action.add,
				});
			});

		const allClientsMapped = syncRequest.updateClients.every((x) => x.id);

		if (!allClientsMapped) {
			this.error =
				'Not all selected names on the title are synced to a client.';
			this.syncStatus = SyncStatus.NOT_SYNCING;

			return;
		}

		syncRequest.titlesToSync = Object.values(this.VM.syncedTitles ?? {}).filter(
			(x) => x !== null,
		);

		const overlappingSyncingTitle = syncRequest.titlesToSync?.find(
			(x) => x.id === this.primaryTitle?.id,
		);

		if (!this.primaryTitle && syncRequest.titlesToSync.length > 0) {
			const titleToBePrimary =
				syncRequest.titlesToSync.find(
					(x) =>
						(x as CondoUnit)?.unitType === UnitType.DWELLING_UNIT || x.primary,
				) ?? syncRequest.titlesToSync[0];
			titleToBePrimary.primary = true;
		} else if (overlappingSyncingTitle) {
			overlappingSyncingTitle.primary = true;
		}

		if (
			syncRequest?.updateClients?.length === 0 &&
			syncRequest?.titlesToSync?.length === 0
		) {
			this.syncStatus = SyncStatus.SYNCED;
			this.onSyncSuccess.emit(true);
			return;
		}

		try {
			await this.teranetRepo.SyncDetails(
				syncRequest,
				this.matterId,
				this.fileType,
			);
			this.syncStatus = SyncStatus.SYNCED;
			this.onSyncSuccess.emit(true);

			[...syncRequest.titlesToSync].forEach((x) => {
				this.setPinToSynced(x.pinNumber);
			});
		} catch (err) {
			this.error = 'Unable to sync due to unknown error. Please retry';
			console.log('Error syning data to file from teranet', err);
			this.syncStatus = SyncStatus.NOT_SYNCING;
			LogRocket.error('Unable to sync teranet information', err);
		}
	}

	setCurrentClient = (value: BaseClient, index, newValue: string) => {
		const updatedSellerActions = [...this.VM.sellerActions];

		updatedSellerActions[index].selected = value.id;
		updatedSellerActions[index].newValue = newValue;
		updatedSellerActions[index].sync = true;

		this.VM = {
			sellerActions: updatedSellerActions,
		};
	};

	addClient = (value: BaseClient, index) => {
		const updatedSellerActions = [...this.VM.sellerActions];

		updatedSellerActions[index].selected = value.id;
		updatedSellerActions[index].add = true;

		this.VM = {
			sellerActions: updatedSellerActions,
		};
	};

	removeClientSelection = (index) => {
		const updatedSellerActions = [...this.VM.sellerActions];

		updatedSellerActions[index].selected = null;
		updatedSellerActions[index].add = false;

		this.VM = {
			sellerActions: updatedSellerActions,
		};
	};

	createClient = (value, index) => {
		const data: CreateClientMiniModalData = {
			client: {
				name: value,
			},
			matterClientRelationship: {
				clientTypes: [
					[MatterFileType.SALE, MatterFileType.PURCHASE].includes(this.fileType)
						? MatterClientType.SELLER
						: MatterClientType.BORROWER,
				],
			},
			callbackFn: async (clientCreationData: ClientCreationData) =>
				new Promise((resolve) => {
					if (clientCreationData?.client?.id) {
						const updatedSellerActions = [...this.VM.sellerActions];

						updatedSellerActions[index].selected = clientCreationData.client.id;

						this.VM = {
							sellerActions: updatedSellerActions,
						};

						this.matterClientDispatch.CreateAndAddClientToMatter(
							clientCreationData,
						);
					}

					setTimeout(() => {
						resolve();
					}, 1500);
				}),
		};
		this.matDialog.open(CreateClientMiniModalComponent, { data });
	};
}
